import {
  EDataSelectSearchMode,
  EDataSelectSourceType,
  IDataSelectApiSourceApiSearch,
  IDataSelectStaticSource,
} from "../../common/DataSelect/dataselector.interface";
import { ICreateArea, ICreateAreaByTypeOfService, ICreateAreasByCategories, ICreateAreasBySquareFootage } from "./areas";
import { ICreateCategory } from "./categories";
import { ECurrency, EMonetaryAmountBy } from "./common";
import { ICreateSector } from "./sectors";
import { ICreateSpecialRequest, ICreateSpecialRequestsByServices } from "./special-requests";
import { ISquareFootage } from "./square-footage";
import { ICreateTask } from "./tasks";
import { ICreateTypeOfPlace } from "./type-of-place";
import { ICreateTypeOfService, ICreateTypeOfServiceByCategory } from "./type-of-service";

const defaultApiUrl = process.env.REACT_APP_API_URL_SECTOR_WORKFLOWS;

export enum EFieldType {
  TEXT = "text",
  NUMBER = "number",
  IMAGE = "image",
  OBJECT = "object",
  SELECT = "select",
}

export interface IFormSelectOption {
  value: string;
  label: string;
}

export interface IContextSelectionMap {
  [key: string]: string | null;
}

export interface IFormState {
  [key: string]: any;
}

export interface ISelectorOption {
  id: string;
  label: string;
}

export interface IEntityNavigatorProps {
  nodes: IWorkflowNodeConfig[];
  selectedKey: string;
  onSelect: (nodeKey: string) => void;
}

export interface IContextSelectorBarProps {
  selectors?: IWorkflowSelector[];
  values: IContextSelectionMap;
  optionsMap: Record<string, ISelectorOption[]>;
  onChange: (key: string, value: string) => void;
}

export enum WorkflowEditorMode {
  Catalog = "catalog",
  Relation = "relation",
  ConditionalRelation = "conditional_relation",
}

export enum WorkflowNodeGroup {
  Catalogs = "catalogs",
  Relationships = "relationships",
  Rules = "rules",
}

export enum WorkflowMutationMethod {
  Post = "POST",
  Put = "PUT",
  Patch = "PATCH",
  Delete = "DELETE",
}

export interface IEntitySelectorConfig {
  key: string;
  label: string;
  placeholder: string;
}

export interface IFieldConfig {
  key: string;
  label: string;
  placeholder?: string;
  type?: string;
}

// Relationship nodes
export interface IContextSelectorDefinition {
  key: string;
  label: string;
  placeholder?: string;
  queryParamKey?: string;
  fieldKey?: string;
  source: IDataSelectApiSourceApiSearch | IDataSelectStaticSource;
}

export enum EFieldSourceType {
  STATIC = "static",
  API = "api",
}

export interface IFieldStaticSourceConfig {
  type: EFieldSourceType.STATIC;
  options: ISelectorOption[];
}

export interface IFieldRelationConfig {
  source: IDataSelectApiSourceApiSearch | IDataSelectStaticSource;
}

export interface IBaseFieldDefinition<TRecord = Record<string, unknown>> {
  key: keyof TRecord | string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  showInRelationCard?: boolean;
}

export interface ITextFieldDefinition<TRecord = Record<string, unknown>> extends IBaseFieldDefinition<TRecord> {
  type: "text";
}

export interface INumberFieldDefinition<TRecord = Record<string, unknown>> extends IBaseFieldDefinition<TRecord> {
  type: "number";
}

export interface ISelectFieldDefinition<TRecord = Record<string, unknown>> extends IBaseFieldDefinition<TRecord> {
  type: "select";
  options: ISelectorOption[];
}

export interface IRelationFieldDefinition<TRecord = Record<string, unknown>> extends IBaseFieldDefinition<TRecord> {
  type: "relation";
  relation: IFieldRelationConfig;
}

export interface IObjectFieldDefinition<TRecord = Record<string, unknown>> extends IBaseFieldDefinition<TRecord> {
  type: "object";
  fields: IFieldDefinition[];
}

export interface IArrayFieldDefinition<TRecord = Record<string, unknown>> extends IBaseFieldDefinition<TRecord> {
  type: "array";
  itemType?: EArrayItemType;
}

export interface IImageFieldDefinition<TRecord = Record<string, unknown>> extends IBaseFieldDefinition<TRecord> {
  type: "image";
}

export enum EArrayItemType {
  STRING = "string",
  NUMBER = "number",
}

export type IFieldDefinition<TRecord = Record<string, unknown>> =
  | ITextFieldDefinition<TRecord>
  | INumberFieldDefinition<TRecord>
  | ISelectFieldDefinition<TRecord>
  | IRelationFieldDefinition<TRecord>
  | IObjectFieldDefinition<TRecord>
  | IArrayFieldDefinition<TRecord>
  | IImageFieldDefinition<TRecord>;

export interface IWorkflowNodeRequestConfig {
  uri: string;
}

export interface IWorkflowSelector {
  key: string;
  label: string;
  placeholder?: string;
  queryParamKey?: string;
  fieldKey?: string;
  source: IDataSelectApiSourceApiSearch | IDataSelectStaticSource;
}

export interface IWorkflowNodeConfig {
  key: string;
  title: string;
  subtitle: string;
  group: WorkflowNodeGroup;
  mode: WorkflowEditorMode;
  fields?: IFieldDefinition<any>[];
  selectors?: IContextSelectorDefinition[];
  binding?: {
    key: string;
    requestConfig: IWorkflowNodeRequestConfig;
  };
  requestConfig?: IWorkflowNodeRequestConfig;
  allowDelete?: boolean;
  conditionalRelationConfig?: IConditionalRelationFetchConfig;

  relationshipConfig?: IRelationshipManagerConfig;
}

export const areaFields: IFieldDefinition<ICreateArea>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter area name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter area description",
    type: "text",
  },
  {
    key: "imageUrl",
    label: "Image",
    type: "image",
  },
  {
    key: "baseDurationInMinutes",
    label: "Base Duration In Minutes",
    type: "number",
  },
];

export const categoriesFields: IFieldDefinition<ICreateCategory>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter category name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter category description",
    type: "text",
  },
  {
    key: "imageUrl",
    label: "Image",
    type: "image",
  },
];

export const objectsFields: IFieldDefinition<IObject>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter object name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter object description",
    type: "text",
  },
  {
    key: "imageUrl",
    label: "Image",
    type: "image",
  },
  {
    key: "cost",
    label: "Cost",
    type: "object",
    fields: [
      {
        key: "by",
        label: "Cost By",
        type: "relation",
        required: true,
        relation: {
          source: {
            type: EDataSelectSourceType.STATIC,
            options: [
              {
                uid: EMonetaryAmountBy.MONTHLY,
                label: "Monthly",
                raw: { uid: EMonetaryAmountBy.MONTHLY, name: "Monthly" },
              },
              {
                uid: EMonetaryAmountBy.YEARLY,
                label: "Yearly",
                raw: { uid: EMonetaryAmountBy.YEARLY, name: "Yearly" },
              },
              {
                uid: EMonetaryAmountBy.UNIQUE,
                label: "Unique",
                raw: { uid: EMonetaryAmountBy.UNIQUE, name: "Unique" },
              },
              {
                uid: EMonetaryAmountBy.PER_ITEM,
                label: "Per Item",
                raw: { uid: EMonetaryAmountBy.PER_ITEM, name: "Per Item" },
              },
              {
                uid: EMonetaryAmountBy.PER_SQUARE_FOOT,
                label: "Per Square Foot",
                raw: { uid: EMonetaryAmountBy.PER_SQUARE_FOOT, name: "Per Square Foot" },
              },
              {
                uid: EMonetaryAmountBy.PER_CAR_SPACE,
                label: "Per Car Space",
                raw: { uid: EMonetaryAmountBy.PER_CAR_SPACE, name: "Per Car Space" },
              },
              {
                uid: EMonetaryAmountBy.PER_FLOOR,
                label: "Per Floor",
                raw: { uid: EMonetaryAmountBy.PER_FLOOR, name: "Per Floor" },
              },
            ],
          },
        },
        placeholder: "Select cost by",
      },
      {
        key: "value",
        label: "Cost Value",
        type: "number",
        required: true,
        placeholder: "Enter cost value",
      },
      {
        key: "currency",
        label: "Currency",
        type: "relation",
        required: true,
        relation: {
          source: {
            type: EDataSelectSourceType.STATIC,
            options: [
              {
                uid: ECurrency.USD,
                label: "USD",
                raw: { uid: ECurrency.USD, name: "USD" },
              },
              {
                uid: ECurrency.MXN,
                label: "MXN",
                raw: { uid: ECurrency.MXN, name: "MXN" },
              },
            ],
          },
        },
        placeholder: "Select currency",
      },
    ],
  },
  {
    key: "durationInMinutes",
    label: "Duration In Minutes",
    type: "number",
  },
];

export const sectorFields: IFieldDefinition<ICreateSector>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter sector name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter sector description",
    type: "text",
  },
];

export const specialRequestFields: IFieldDefinition<ICreateSpecialRequest>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter special request name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter special request description",
    type: "text",
  },
  {
    key: "imageUrl",
    label: "Image",
    type: "image",
  },
];

export const taskFields: IFieldDefinition<ICreateTask>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter task name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter task description",
    type: "text",
  },
];

export const typeOfPlacesFields: IFieldDefinition<ICreateTypeOfPlace>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter type of place name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter type of place description",
    type: "text",
  },
  {
    key: "categoryUuid",
    label: "Category",
    placeholder: "Search category",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/categories`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "imageUrl",
    label: "Image",
    type: "image",
  },
];

export const typeOfServiceFields: IFieldDefinition<ICreateTypeOfService>[] = [
  {
    key: "name",
    label: "Name",
    placeholder: "Enter type of service name",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter type of service description",
    type: "text",
  },
  {
    key: "imageUrl",
    label: "Image",
    type: "image",
  },
];

export const squareFootageFields: IFieldDefinition<ISquareFootage>[] = [
  {
    key: "range",
    label: "Range",
    placeholder: "Select square footage range",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    placeholder: "Enter square footage description",
    type: "text",
  },
  {
    key: "increaseBaseDurationMinutesPercentage",
    label: "Increase Base Duration Minutes Percentage",
    type: "number",
  },
];

export const areasByTypeOfServicesFields: IFieldDefinition<ICreateAreaByTypeOfService>[] = [
  {
    key: "areaUid",
    label: "Area",
    placeholder: "Search area",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/areas`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "typeOfServiceUid",
    label: "Service",
    placeholder: "Search service",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/type-of-service`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "tags",
    label: "Tags",
    placeholder: "Enter tags separated by commas",
    type: "array",
    itemType: EArrayItemType.STRING,
  },
  {
    key: "cost",
    label: "Cost",
    type: "object",
    fields: [
      {
        key: "by",
        label: "Cost By",
        type: "relation",
        required: true,
        placeholder: "Select cost by",
        relation: {
          source: {
            type: EDataSelectSourceType.STATIC,
            options: [
              {
                uid: EMonetaryAmountBy.MONTHLY,
                label: "Monthly",
                raw: { uid: EMonetaryAmountBy.MONTHLY, name: "Monthly" },
              },
              {
                uid: EMonetaryAmountBy.YEARLY,
                label: "Yearly",
                raw: { uid: EMonetaryAmountBy.YEARLY, name: "Yearly" },
              },
              {
                uid: EMonetaryAmountBy.UNIQUE,
                label: "Unique",
                raw: { uid: EMonetaryAmountBy.UNIQUE, name: "Unique" },
              },
              {
                uid: EMonetaryAmountBy.PER_ITEM,
                label: "Per Item",
                raw: { uid: EMonetaryAmountBy.PER_ITEM, name: "Per Item" },
              },
              {
                uid: EMonetaryAmountBy.PER_SQUARE_FOOT,
                label: "Per Square Foot",
                raw: { uid: EMonetaryAmountBy.PER_SQUARE_FOOT, name: "Per Square Foot" },
              },
              {
                uid: EMonetaryAmountBy.PER_CAR_SPACE,
                label: "Per Car Space",
                raw: { uid: EMonetaryAmountBy.PER_CAR_SPACE, name: "Per Car Space" },
              },
              {
                uid: EMonetaryAmountBy.PER_FLOOR,
                label: "Per Floor",
                raw: { uid: EMonetaryAmountBy.PER_FLOOR, name: "Per Floor" },
              },
            ],
          },
        },
      },
      {
        key: "value",
        label: "Cost Value",
        type: "number",
        required: true,
        placeholder: "Enter cost value",
      },
      {
        key: "currency",
        label: "Currency",
        type: "relation",
        required: true,
        placeholder: "Select currency",
        relation: {
          source: {
            type: EDataSelectSourceType.STATIC,
            options: [
              {
                uid: ECurrency.USD,
                label: "USD",
                raw: { uid: ECurrency.USD, name: "USD" },
              },
              {
                uid: ECurrency.MXN,
                label: "MXN",
                raw: { uid: ECurrency.MXN, name: "MXN" },
              },
            ],
          },
        },
      },
    ],
  },
];

export const areasByCategoriesFields: IFieldDefinition<ICreateAreasByCategories>[] = [
  {
    key: "areaUid",
    label: "Area",
    placeholder: "Search area",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/areas`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "categoryUid",
    label: "Category",
    placeholder: "Search category",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/categories`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
];

export const specialRequestsByServicesFields: IFieldDefinition<ICreateSpecialRequestsByServices>[] = [
  {
    key: "specialRequestUid",
    label: "Special Request",
    placeholder: "Search special request",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/special-requests`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "typeOfServiceUid",
    label: "Service",
    placeholder: "Search service",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/type-of-service`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "increaseBasePricePercentage",
    label: "Increase Base Price Percentage",
    type: "object",
    fields: [
      {
        key: "0-1000",
        label: "0-1000",
        type: "number",
      },
      {
        key: "1001-2000",
        label: "1001-2000",
        type: "number",
      },
      {
        key: "2001-3000",
        label: "2001-3000",
        type: "number",
      },
      {
        key: "3001-4000",
        label: "3001-4000",
        type: "number",
      },
      {
        key: "4001-5000",
        label: "4001-5000",
        type: "number",
      },
      {
        key: "5001",
        label: "5001+",
        type: "number",
      },
    ],
  },
  {
    key: "tags",
    label: "Tags",
    placeholder: "Enter tags",
    type: "array",
    itemType: EArrayItemType.STRING,
  },
];

export const areasBySquareFootageFields: IFieldDefinition<ICreateAreasBySquareFootage>[] = [
  {
    key: "areaUid",
    label: "Area",
    placeholder: "Search area",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/areas`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "squareFootageUid",
    label: "Square Footage",
    placeholder: "Search square footage",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/square-footage`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "range",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "range",
        minSearchLength: 1,
        enabled: true,
        queryParams: {
          range: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "quantity",
    label: "Quantity",
    type: "number",
  },
  {
    key: "tags",
    label: "Tags",
    placeholder: "Enter tags",
    type: "array",
    itemType: EArrayItemType.STRING,
  },
];

export const typeOfServicesByCategoriesFields: IFieldDefinition<ICreateTypeOfServiceByCategory>[] = [
  {
    key: "categoryUid",
    label: "Category",
    placeholder: "Search category",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/categories`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
  {
    key: "typeOfServiceUid",
    label: "Service",
    placeholder: "Search service",
    type: "relation",
    required: true,
    relation: {
      source: {
        type: EDataSelectSourceType.API,
        url: `${defaultApiUrl}/type-of-service`,
        bearerToken: "",
        valueKey: "uid",
        labelKey: "name",
        pageSize: 10,
        searchMode: EDataSelectSearchMode.API,
        searchParamKey: "name",
        minSearchLength: 2,
        enabled: true,
        queryParams: {
          name: "",
        },
      },
    },
    showInRelationCard: true,
  },
];

// WORKFLOW NODES
export const workflowNodes: IWorkflowNodeConfig[] = [
  // Catalogs
  {
    key: "tasks",
    title: "Tasks",
    subtitle: "Manage task catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: taskFields,
    allowDelete: true,
    requestConfig: { uri: "/tasks" },
  },
  {
    key: "objects",
    title: "Objects",
    subtitle: "Manage object catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: objectsFields,
    allowDelete: true,
    requestConfig: { uri: "/objects" },
  },
  {
    key: "areas",
    title: "Areas",
    subtitle: "Manage area catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: areaFields,
    allowDelete: false,
    requestConfig: { uri: "/areas" },
  },
  {
    key: "specialRequests",
    title: "Special Requests",
    subtitle: "Manage special request catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: specialRequestFields,
    allowDelete: false,
    requestConfig: { uri: "/special-requests" },
  },
  {
    key: "squareFootage",
    title: "Square Footage",
    subtitle: "Manage square footage catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: squareFootageFields,
    allowDelete: false,
    requestConfig: { uri: "/square-footage" },
  },
  {
    key: "typeOfPlaces",
    title: "Type Of Places",
    subtitle: "Manage type of place catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: typeOfPlacesFields,
    allowDelete: false,
    requestConfig: { uri: "/type-of-places" },
  },
  {
    key: "typeOfServices",
    title: "Type of Services",
    subtitle: "Manage service catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: typeOfServiceFields,
    allowDelete: false,
    requestConfig: { uri: "/type-of-service" },
  },
  {
    key: "categories",
    title: "Categories",
    subtitle: "Manage category catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: categoriesFields,
    allowDelete: true,
    requestConfig: { uri: "/categories" },
  },
  {
    key: "sectors",
    title: "Sectors",
    subtitle: "Manage sector catalog records",
    group: WorkflowNodeGroup.Catalogs,
    mode: WorkflowEditorMode.Catalog,
    fields: sectorFields,
    allowDelete: false,
    requestConfig: { uri: "/sectors" },
  },
  // Relationships
  {
    key: "typeOfServicesByCategories",
    title: "Type Of Services By Categories",
    subtitle: "Manage service and category relations",
    group: WorkflowNodeGroup.Relationships,
    mode: WorkflowEditorMode.Relation,
    fields: typeOfServicesByCategoriesFields,
    allowDelete: true,
    requestConfig: { uri: "/type-of-service-by-categories" },
    relationshipConfig: {
      base: {
        label: "Category",
        pluralLabel: "Categories",
        fieldKey: "categoryUid",
        requestConfig: { uri: "/categories" },
        searchParamKey: "name",
      },
      related: {
        label: "Type Of Service",
        pluralLabel: "Type Of Services",
        fieldKey: "typeOfServiceUid",
        requestConfig: { uri: "/type-of-service" },
        searchParamKey: "name",
      },
      saveConfig: {
        createUri: "/type-of-service-by-categories",
        updateUri: "/type-of-service-by-categories",
        deleteUri: "/type-of-service-by-categories",
      },
    },
  },
  {
    key: "areasByTypeOfServices",
    title: "Areas By Services",
    subtitle: "Manage area and service relations",
    group: WorkflowNodeGroup.Relationships,
    mode: WorkflowEditorMode.Relation,
    fields: areasByTypeOfServicesFields,
    allowDelete: true,
    requestConfig: { uri: "/area-by-type-of-services" },
    relationshipConfig: {
      base: {
        label: "Service",
        pluralLabel: "Services",
        fieldKey: "typeOfServiceUid",
        requestConfig: { uri: "/type-of-service" },
        searchParamKey: "name",
      },
      related: {
        label: "Area",
        pluralLabel: "Areas",
        fieldKey: "areaUid",
        requestConfig: { uri: "/areas" },
        searchParamKey: "name",
      },
      saveConfig: {
        createUri: "/area-by-type-of-services",
        updateUri: "/area-by-type-of-services",
        deleteUri: "/area-by-type-of-services",
      },
    },
  },
  {
    key: "areasByCategories",
    title: "Areas By Categories",
    subtitle: "Manage area and category relations",
    group: WorkflowNodeGroup.Relationships,
    mode: WorkflowEditorMode.Relation,
    fields: areasByCategoriesFields,
    allowDelete: true,
    requestConfig: { uri: "/areas-by-categories" },
    relationshipConfig: {
      base: {
        label: "Category",
        pluralLabel: "Categories",
        fieldKey: "categoryUid",
        requestConfig: { uri: "/categories" },
        searchParamKey: "name",
      },
      related: {
        label: "Area",
        pluralLabel: "Areas",
        fieldKey: "areaUid",
        requestConfig: { uri: "/areas" },
        searchParamKey: "name",
      },
      saveConfig: {
        createUri: "/areas-by-categories",
        updateUri: "/areas-by-categories",
        deleteUri: "/areas-by-categories",
      },
    },
  },
  {
    key: "areasBySquareFootage",
    title: "Areas By Square Footage",
    subtitle: "Manage area and square footage relations",
    group: WorkflowNodeGroup.Relationships,
    mode: WorkflowEditorMode.Relation,
    fields: areasBySquareFootageFields,
    allowDelete: true,
    requestConfig: { uri: "/areas-by-square-footage" },
    relationshipConfig: {
      base: {
        label: "Square Footage",
        pluralLabel: "Square Footages",
        fieldKey: "squareFootageUid",
        requestConfig: { uri: "/square-footage" },
        searchParamKey: "range",
      },
      related: {
        label: "Area",
        pluralLabel: "Areas",
        fieldKey: "areaUid",
        requestConfig: { uri: "/areas" },
        searchParamKey: "name",
      },
      saveConfig: {
        createUri: "/areas-by-square-footage",
        updateUri: "/areas-by-square-footage",
        deleteUri: "/areas-by-square-footage",
      },
    },
  },
  {
    key: "specialRequestsByServices",
    title: "Special Requests By Services",
    subtitle: "Manage special request and service relations",
    group: WorkflowNodeGroup.Relationships,
    mode: WorkflowEditorMode.Relation,
    fields: specialRequestsByServicesFields,
    allowDelete: true,
    requestConfig: { uri: "/special-requests-by-services" },
    relationshipConfig: {
      base: {
        label: "Type Of Service",
        pluralLabel: "Type Of Services",
        fieldKey: "typeOfServiceUid",
        requestConfig: { uri: "/type-of-service" },
        searchParamKey: "name",
      },
      related: {
        label: "Special Request",
        pluralLabel: "Special Requests",
        fieldKey: "specialRequestUid",
        requestConfig: { uri: "/special-requests" },
        searchParamKey: "name",
      },
      saveConfig: {
        createUri: "/special-requests-by-services",
        updateUri: "/special-requests-by-services",
        deleteUri: "/special-requests-by-services",
      },
    },
  },
  // Rules
  {
    key: "service-area-tasks",
    title: "Service Area Tasks",
    subtitle: "Attach tasks to a selected service and area context",
    group: WorkflowNodeGroup.Rules,
    mode: WorkflowEditorMode.ConditionalRelation,
    selectors: [
      {
        key: "serviceUid",
        label: "Service",
        placeholder: "Select a service",
        queryParamKey: "typeOfServiceUid",
        fieldKey: "typeOfServiceUid",
        source: {
          type: EDataSelectSourceType.API,
          url: `${defaultApiUrl}/type-of-service`,
          bearerToken: "",
          valueKey: "uid",
          labelKey: "name",
          pageSize: 10,
          searchMode: EDataSelectSearchMode.API,
          searchParamKey: "name",
          minSearchLength: 2,
          enabled: true,
          queryParams: {
            name: "",
          },
        },
      },
      {
        key: "areaUid",
        label: "Area",
        placeholder: "Select an area",
        queryParamKey: "areaUid",
        fieldKey: "areaUid",
        source: {
          type: EDataSelectSourceType.API,
          url: `${defaultApiUrl}/areas`,
          bearerToken: "",
          valueKey: "uid",
          labelKey: "name",
          pageSize: 10,
          searchMode: EDataSelectSearchMode.API,
          searchParamKey: "name",
          minSearchLength: 2,
          enabled: true,
          queryParams: {
            name: "",
          },
        },
      },
    ],
    binding: {
      key: "task",
      requestConfig: {
        uri: "/tasks",
      },
    },
    allowDelete: true,
    requestConfig: {
      uri: "/task-area-services",
    },
    conditionalRelationConfig: {
      fetchOnMount: true,
      requireAllSelectorsBeforeFetch: true,
      pageSize: 10,
      saveConfig: {
        createBulkUri: "/task-area-services/bulk",
        deleteBulkUri: "/task-area-services/bulk",
      },
    },
  },
];

export interface ICreateCatalogRecordBody {
  [key: string]: unknown;
}

export interface IBuildPatchBodyParams {
  initialValues: IFormState;
  currentValues: IFormState;
}

export interface IBuildPatchBodyResult {
  body: Record<string, unknown> | FormData;
  isMultipart: boolean;
  hasChanges: boolean;
}

export interface IMonetaryAmount {
  by: EMonetaryAmountBy;
  value: number;
  currency: ECurrency;
}

export interface IObject {
  name: string;
  description: string;
  imageUrl: string;
  cost: IMonetaryAmount;
  durationInMinutes: number;
}

export interface IWorkflowRelationDisplayItem {
  label: string;
  path: string;
  imagePath?: string;
}

export interface IWorkflowNodeRelationDisplay {
  items: IWorkflowRelationDisplayItem[];
}

export interface IRecordPresentationProps {
  record: IWorkflowBaseRecord;
  fields?: IFieldDefinition<any>[];
  selected?: boolean;
}

export interface IConditionalRelationFetchConfig {
  requireAllSelectorsBeforeFetch?: boolean;
  fetchOnMount?: boolean;
  pageSize?: number;
  saveConfig?: IConditionalRelationSaveConfig;
}

export interface IWorkflowNodeConditionalRelationConfig extends IWorkflowNodeConfig {
  selectors?: IContextSelectorDefinition[];
  requestConfig: IWorkflowNodeRequestConfig;
  conditionalRelationConfig?: IConditionalRelationFetchConfig;
}

export interface IShouldFetchConditionalRelationArgs {
  selectors?: IContextSelectorDefinition[];
  contextSelection: IContextSelectionMap;
  config?: IConditionalRelationFetchConfig;
}

export interface IBuildConditionalRelationQueryParamsArgs {
  selectors?: IContextSelectorDefinition[];
  contextSelection: IContextSelectionMap;
  page: number;
  pageSize: number;
}

export interface IConditionalRelationSaveConfig {
  createBulkUri?: string;
  deleteBulkUri?: string;
}

export interface IConditionalRelationBulkCreateItem {
  taskUid: string;
  areaUid: string;
  typeOfServiceUid: string;
}

export interface IConditionalRelationBulkCreatePayload {
  items: IConditionalRelationBulkCreateItem[];
}

export interface IConditionalRelationBulkDeletePayload {
  uids: string[];
}

export interface IWorkflowBaseRecord {
  uid: string;
  name?: string;
  description?: string;
  imageUrl?: string;
  range?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IServiceRecord extends IWorkflowBaseRecord {
  code: string;
}

export interface IAreaRecord extends IWorkflowBaseRecord {
  icon?: string;
}

export interface ITaskRecord extends IWorkflowBaseRecord {
  code: string;
}

export interface IServiceAreaRelationRecord {
  uid: string;
  typeOfServiceUid: string;
  areaUid: string;
  tags: string[];
}

export interface IServiceAreaTaskRelationRecord {
  uid: string;
  typeOfServiceUid: string;
  areaUid: string;
  taskUid: string;
}

// mock data

export const servicesMock: IServiceRecord[] = [
  {
    uid: "svc-deep-cleaning",
    name: "Deep Cleaning",
    description: "Detailed service for full-property cleaning workflows.",
    code: "DEEP",
  },
  {
    uid: "svc-regular-cleaning",
    name: "Regular Cleaning",
    description: "Recurring service with standard maintenance tasks.",
    code: "REGULAR",
  },
];

export const areasMock: IAreaRecord[] = [
  {
    uid: "area-living-room",
    name: "Living Room",
    description: "Main shared space for seating and common activity.",
  },
  {
    uid: "area-kitchen",
    name: "Kitchen",
    description: "Food preparation and appliance area.",
  },
];

export const tasksMock: ITaskRecord[] = [
  {
    uid: "task-clean-window-spots",
    name: "Clean Window Spots",
    description: "Spot clean visible marks on glass and mirrors.",
    code: "TASK_WINDOW_SPOTS",
  },
  {
    uid: "task-dust-furniture",
    name: "Dust Furniture",
    description: "Remove dust from accessible furniture surfaces.",
    code: "TASK_DUST_FURNITURE",
  },
];

export const serviceAreasMock: IServiceAreaRelationRecord[] = [
  {
    uid: "sar-1",
    typeOfServiceUid: "svc-deep-cleaning",
    areaUid: "area-living-room",
    tags: ["priority"],
  },
  {
    uid: "sar-2",
    typeOfServiceUid: "svc-regular-cleaning",
    areaUid: "area-kitchen",
    tags: ["maintenance"],
  },
];

export const serviceAreaTasksMock: IServiceAreaTaskRelationRecord[] = [
  {
    uid: "sat-1",
    typeOfServiceUid: "svc-deep-cleaning",
    areaUid: "area-living-room",
    taskUid: "task-clean-window-spots",
  },
  {
    uid: "sat-2",
    typeOfServiceUid: "svc-deep-cleaning",
    areaUid: "area-living-room",
    taskUid: "task-dust-furniture",
  },
];

export interface IRelationshipEntityConfig {
  label: string;
  pluralLabel: string;
  fieldKey: string;
  requestConfig: IWorkflowNodeRequestConfig;
  searchParamKey?: string;
}

export interface IRelationshipSaveConfig {
  createUri?: string;
  updateUri?: string;
  deleteUri?: string;
}

export interface IRelationshipManagerConfig {
  base: IRelationshipEntityConfig;
  related: IRelationshipEntityConfig;
  saveConfig?: IRelationshipSaveConfig;
}
